import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../../commonComponents/translate';
import CreateNote from './CreateNote';
import Notifications from '../../../../services/notifications';
import OrderProp from '../../propTypes/orderProp';
import ActivityNotesList from './ActivityNotesList';
import { compose } from '../../../../services/utilityFunctions';
import withCSRF from '../../../../commonComponents/withCSRF';

function ActivityNotesTab(props) {
  const { _csrf, entity } = props;
  const { _id } = entity;

  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState('');

  // List state
  const [list, setList] = useState([]);
  const [metaInfoTotal, setMetaInfoTotal] = useState(null);
  const [page, setPage] = useState(1);
  const [hasEnded, setHasEnded] = useState(true);
  const isFetchingRef = useRef(false);

  /**
   * Updates the state of note
   * @type {function(*): void}
   */
  const handleNoteChange = useCallback(
    updatedNote => setNote(updatedNote),
    [setNote],
  );

  /**
   * Get the activities & notes for this entity, manages also the pagination
   * @type {Promise<{pageNumber: *, pages: any, newList}>}
   */
  const fetchActivityNotesData = useCallback(async (pageNumber, initList) => {
    try {
      setIsLoading(true);
      const { data: response } = await axios.get(
        `/table/market/opportunity/${_id}/activity-notes/list?sort=-createdAt`,
        {
          params: {
            pagination: { perpage: 8, page: pageNumber },
            populate: ['user'],
          },
          headers: {
            'X-Gamification-API': 'Games',
            'Accept-Version': '1.0.0',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      const { data, meta } = response;
      return {
        pageNumber,
        newList: [...initList, ...data],
        pages: meta?.pages,
      };
    } catch (e) {
      Notifications.showErrorFromRequest(e, 'opportunity.notes.list.error');
      return {
        pageNumber: 1,
        newList: initList,
        pages: {},
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Post a new note in opportunity
   * @type {(function(): Promise<void>)|*}
   */
  const onAddNote = useCallback(async () => {
    try {
      setIsLoading(true);
      await axios.post(
        `/api/market/opportunity/${entity?._id}/note`,
        {
          note,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      const { newList, pages } = await fetchActivityNotesData(1, []);
      setNote('');
      setMetaInfoTotal(pages);
      setList(newList);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'notes.error');
    } finally {
      setIsLoading(false);
    }
  }, [note, page, list, fetchActivityNotesData]);

  // region handle list

  /** Gets first batch of items for page 1 * */
  useEffect(() => {
    (async () => {
      const { newList, pages } = await fetchActivityNotesData(1, []);
      setMetaInfoTotal(pages);
      setList(newList);
      setPage(1);
    })();
  }, [fetchActivityNotesData, setMetaInfoTotal, setList, setPage]);

  /** Manages when list gets to the end & needs to fetch more items */
  const handleReachedEnd = useCallback(async () => {
    if (isFetchingRef.current) return; // Exit if a fetch is in progress

    isFetchingRef.current = true; // Set fetching status to true

    setHasEnded(page + 1 <= metaInfoTotal);
    if (!metaInfoTotal || page + 1 <= metaInfoTotal) {
      const { pageNumber, newList, pages } = await fetchActivityNotesData(
        page + 1,
        list,
      );
      setPage(pageNumber);
      setMetaInfoTotal(pages);
      setList(newList);
    }

    isFetchingRef.current = false; // Reset fetching status after fetch is done
  }, [setList, setPage, setMetaInfoTotal, metaInfoTotal, page, list]);
  // endregion handle list

  return (
    <div className="m-portlet m-portlet-rounded activity-notes-section">
      <CreateNote
        onAddNote={onAddNote}
        note={note}
        isLoading={isLoading}
        onChange={handleNoteChange}
      />
      <ActivityNotesList
        list={list}
        onReachEnd={handleReachedEnd}
        hasEnded={hasEnded}
        isLoading={isLoading}
      />
    </div>
  );
}

ActivityNotesTab.propTypes = {
  _csrf: PropTypes.string.isRequired,
  entity: PropTypes.shape(OrderProp.propType).isRequired,
};

export default compose(translate, withCSRF)(ActivityNotesTab);
